/**
 * Explore Programs Tab - Browse and filter programs
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { ProgramFilters, ProgramList } from '../../program/components';
import { programAPI } from '../../program/api/program-api';
import { SEO, Header } from '../../../shared/components';
import { ExploreTabs } from '../components';
import type { Program, ProgramFilters as ProgramFiltersType, ProgramFilterOptions } from '../../program/types';

const ExploreProgramsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [filterOptions, setFilterOptions] = useState<ProgramFilterOptions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
    limit: 20
  });

  // Parse filters from URL
  const getFiltersFromURL = (): ProgramFiltersType => {
    return {
      field: searchParams.get('field') || undefined,
      degreeType: searchParams.get('degree_type') || undefined,
      country: searchParams.get('country') || undefined,
      isOnline: searchParams.get('is_online') === 'true' ? true : searchParams.get('is_online') === 'false' ? false : undefined,
      page: searchParams.get('page') ? parseInt(searchParams.get('page')!) : 1,
      sort: searchParams.get('sort') || 'university_name'
    };
  };

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options = await programAPI.getFilterOptions();
        setFilterOptions(options);
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    };
    loadFilterOptions();
  }, []);

  // Load programs when filters change
  useEffect(() => {
    const loadPrograms = async () => {
      setIsLoading(true);
      try {
        const filters = getFiltersFromURL();
        const result = await programAPI.browsePrograms(filters);
        setPrograms(result.programs);
        setPagination(result.pagination);
      } catch (err) {
        console.error('Failed to load programs:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadPrograms();
  }, [searchParams]);

  // Update URL when filters change
  const handleFilterChange = (filters: ProgramFiltersType) => {
    const params: any = {};

    if (filters.field) params.field = filters.field;
    if (filters.degreeType) params.degree_type = filters.degreeType;
    if (filters.country) params.country = filters.country;
    if (typeof filters.isOnline === 'boolean') params.is_online = filters.isOnline.toString();
    if (filters.sort && filters.sort !== 'university_name') params.sort = filters.sort;
    if (filters.page && filters.page !== 1) params.page = filters.page.toString();

    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const filters = getFiltersFromURL();
    handleFilterChange({ ...filters, page });
  };

  const handleResetFilters = () => {
    setSearchParams({});
  };

  const handleProgramClick = (programId: number) => {
    navigate(`/program/${programId}`, {
      state: { from: `/explore/programs?${searchParams.toString()}` }
    });
  };

  if (!filterOptions) {
    return (
      <>
        <SEO title="Browse Programs" />
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="flex items-center gap-3">
            <Loader2 className="h-7 w-7 animate-spin text-success" />
            <span className="text-foreground font-medium">Loading programs...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Explore Programs - 86+ Academic Programs"
        description="Browse 86+ academic programs worldwide. Filter by field, degree type, country, and study mode."
      />

      <Header />
      <ExploreTabs />

      <div className="min-h-screen bg-background">
        {/* Main Content with Layout */}
        <div className="container mx-auto px-6 py-10">
          <div className="grid grid-cols-12 gap-8">
            {/* Sticky Sidebar Filters */}
            <div className="col-span-12 lg:col-span-3">
              <div className="sticky top-24">
                <ProgramFilters
                  filters={getFiltersFromURL()}
                  filterOptions={filterOptions}
                  onFilterChange={handleFilterChange}
                  onReset={handleResetFilters}
                />
              </div>
            </div>

            {/* Program Results */}
            <div className="col-span-12 lg:col-span-9">
              <ProgramList
                programs={programs}
                pagination={pagination}
                isLoading={isLoading}
                onPageChange={handlePageChange}
                onProgramClick={handleProgramClick}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExploreProgramsPage;
